// @levelup/provider-catalog
//
// Single source of truth for the Providers surface contract.
//
// Owns everything that must stay identical across every CRM client
// (types, provider catalog, credential shapes, visibility rules, DTOs).
// Consumers must not fork any of these constants — extend or amend the
// package instead.
//
// Consumers today:
//   • CRM Mobile — `mobile/src/screens/Settings/providers/*` re-exports
//     from here so the mobile Providers surface never drifts.
//
// Consumers pending migration (documented debt):
//   • CRM Web — `frontend/src/components/providers/*` still holds its
//     own inline copies of PROVIDER_OPTIONS / CREDENTIAL_SHAPE /
//     PROVIDER_DISPLAY_NAMES / CHANNEL_LABEL / AWS_SES_REGIONS /
//     isUserVisibleProvider / expandVisibleProviderRows /
//     deriveHealthLevel. Migrating the web surface to consume this
//     package is a one-file swap per consumer and blocks nothing.
//
// NOTHING framework-specific belongs in this package. No React, no
// React Native, no Redux, no icons, no color tokens. Framework
// integration lives in each consumer's UI layer.
export const PROVIDER_OPTIONS = [
    {
        provider: 'twilio',
        label: 'Twilio (SMS / Voice)',
        channels: ['sms', 'voice'],
        credentialFields: [
            { key: 'accountSid', label: 'Account SID' },
            { key: 'apiKey', label: 'Auth Token' },
        ],
        senderIdentityFields: ['fromNumber'],
    },
    {
        provider: 'twilio_whatsapp',
        label: 'Twilio WhatsApp',
        channels: ['whatsapp'],
        credentialFields: [
            { key: 'accountSid', label: 'Account SID' },
            { key: 'apiKey', label: 'Auth Token' },
        ],
        senderIdentityFields: ['fromNumber'],
    },
    {
        provider: 'sendgrid',
        label: 'SendGrid (Email)',
        channels: ['email'],
        credentialFields: [{ key: 'apiKey', label: 'API Key' }],
        senderIdentityFields: ['fromEmail', 'fromName'],
    },
    {
        provider: 'ses',
        label: 'Amazon SES (Email)',
        channels: ['email'],
        credentialFields: [
            { key: 'apiKey', label: 'Access Key ID' },
            { key: 'apiSecret', label: 'Secret Access Key' },
        ],
        senderIdentityFields: ['fromEmail', 'fromName'],
        sesRegion: true,
    },
    {
        provider: 'mailgun',
        label: 'Mailgun (Email)',
        channels: ['email'],
        credentialFields: [{ key: 'apiKey', label: 'API Key' }],
        senderIdentityFields: ['fromEmail', 'fromName'],
    },
    {
        provider: 'elevenlabs',
        label: 'ElevenLabs (Voice AI)',
        channels: ['voice_ai'],
        credentialFields: [{ key: 'apiKey', label: 'API Key' }],
        senderIdentityFields: [],
    },
    {
        provider: 'retell',
        label: 'Retell (Voice AI)',
        channels: ['voice_ai'],
        credentialFields: [{ key: 'apiKey', label: 'API Key' }],
        senderIdentityFields: ['fromNumber'],
    },
];
// Display-name map used by the Edit surface. Includes provider keys
// beyond the Add catalog (`gmail`, `outlook`, etc.) so an existing
// mirrored row of that type still shows a friendly heading, even if
// the client can't render its credential shape yet.
export const PROVIDER_DISPLAY_NAMES = {
    twilio: 'Twilio',
    twilio_whatsapp: 'Twilio WhatsApp',
    sendgrid: 'SendGrid',
    ses: 'Amazon SES',
    mailgun: 'Mailgun',
    gmail: 'Gmail',
    outlook: 'Outlook',
    mailchimp: 'Mailchimp',
    retell: 'Retell AI',
    elevenlabs: 'ElevenLabs',
    vapi: 'Vapi',
    whatsapp: 'WhatsApp',
};
export const CHANNEL_LABEL = {
    email: 'Email',
    voice: 'Voice',
    voice_ai: 'Voice AI',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
};
/**
 * Region list for the SES edit surface. Matches Outbound's own
 * ProviderFormModal region catalog.
 */
export const AWS_SES_REGIONS = [
    { value: 'us-east-1', label: 'US East (N. Virginia) — us-east-1' },
    { value: 'us-east-2', label: 'US East (Ohio) — us-east-2' },
    { value: 'us-west-1', label: 'US West (N. California) — us-west-1' },
    { value: 'us-west-2', label: 'US West (Oregon) — us-west-2' },
    { value: 'eu-west-1', label: 'Europe (Ireland) — eu-west-1' },
    { value: 'eu-west-2', label: 'Europe (London) — eu-west-2' },
    { value: 'eu-central-1', label: 'Europe (Frankfurt) — eu-central-1' },
    { value: 'ap-south-1', label: 'Asia Pacific (Mumbai) — ap-south-1' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore) — ap-southeast-1' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney) — ap-southeast-2' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo) — ap-northeast-1' },
    { value: 'ca-central-1', label: 'Canada (Central) — ca-central-1' },
    { value: 'sa-east-1', label: 'South America (Sao Paulo) — sa-east-1' },
];
/**
 * Maps the credential-field key used in the WRITE payload to the
 * mask-field key delivered on the READ payload (`channel.credentialMask`).
 * Outbound uses distinct names on read vs write so the CRM mirror
 * endpoint's credential firewall accepts the whole payload.
 */
export const MASK_FIELD_BY_CRED = {
    accountSid: 'sidLast4',
    apiKey: 'keyLast4',
    apiSecret: 'secretLast4',
};
export function prettyProviderName(appKey) {
    return PROVIDER_DISPLAY_NAMES[appKey] ?? appKey;
}
export function prettyChannelName(type) {
    return CHANNEL_LABEL[type] ?? type;
}
/**
 * True when this channel MAY carry a recording mode. Voice channels
 * only — SMS/email/whatsapp/voice_ai never do (Outbound rejects
 * `settings.voip.recordingMode` on non-voice providers with a 400).
 * Use this to gate any Recording UI so CRM never renders a control
 * that will 400 on save.
 */
export function channelSupportsRecordingMode(channel) {
    return channel?.type === 'voice';
}
/**
 * Resolve the effective per-provider recording mode from a mirrored
 * channel. Read order:
 *
 *   1. First-class typed field `channel.recordingMode` (preferred; used
 *      once Outbound's mapper starts emitting it).
 *   2. Fallback: `channel.extraSettings.voip.recordingMode` (today's
 *      wire shape — Outbound sanitizes settings and passes `voip`
 *      through unchanged).
 *
 * Legacy values are normalized:
 *   'off'    → 'manual'
 *   'always' → 'automatic'
 *   missing / non-string / non-canonical → null
 *
 * Returns `null` when there is no signal; callers decide whether to
 * default to `'manual'` on write. NEVER throws.
 */
export function readChannelRecordingMode(channel) {
    if (!channel)
        return null;
    const firstClass = channel.recordingMode;
    if (firstClass === 'automatic' || firstClass === 'manual')
        return firstClass;
    const extra = channel.extraSettings;
    if (!extra || typeof extra !== 'object')
        return null;
    const voip = extra.voip;
    if (!voip || typeof voip !== 'object')
        return null;
    const raw = voip.recordingMode;
    if (raw === 'automatic' || raw === 'always')
        return 'automatic';
    if (raw === 'manual' || raw === 'off')
        return 'manual';
    return null;
}
/**
 * Shape lookup for the Edit surface. Returns the fields to render for a
 * given `appKey`; an unknown provider (e.g. `gmail`) returns empty
 * arrays and the Edit form falls back to safe-fields-only (display
 * name / status / sender identity).
 */
export function credentialShapeFor(appKey) {
    const match = PROVIDER_OPTIONS.find((p) => p.provider === appKey);
    if (!match)
        return { credentialFields: [], senderIdentityFields: [] };
    return {
        credentialFields: match.credentialFields,
        senderIdentityFields: match.senderIdentityFields,
        sesRegion: match.sesRegion,
    };
}
// ============================================================
// Visibility + summary utility
// ============================================================
//
// Pure — no framework dependencies. Consumers pass their local
// integration array in; the utility filters + expands + counts.
/**
 * True iff this mirrored provider should appear in the CRM operator
 * Providers list.
 *
 * Rules (order matters):
 *   1. Only Outbound-mirrored rows are candidates — CRM-native
 *      integrations belong on the Integrations page.
 *   2. Rows whose EVERY channel is `warmup` are hidden (Outbound's
 *      native warmup pool).
 *   3. Mixed rows (Twilio row carrying `sms` + `voice` + warmup) STAY
 *      visible.
 */
export function isUserVisibleProvider(i) {
    const sync = i.settings?.sync;
    if (!sync || sync.source !== 'outbound')
        return false;
    const channels = Array.isArray(sync.channels) ? sync.channels : [];
    if (channels.length === 0)
        return true;
    return channels.some((c) => typeof c?.type === 'string' && c.type !== 'warmup');
}
/**
 * Expand each mirrored integration into one row per channel. Warmup
 * channels are skipped; non-Outbound rows are skipped.
 *
 * Generic over the caller's integration type so a consumer that passes
 * in a richer `IntegrationConnection` (e.g. CRM Web's local type with
 * `workspaceId`, `credentials`, etc.) receives rows carrying that
 * exact type — no downcast at the call site.
 */
export function expandVisibleProviderRows(integrations) {
    const out = [];
    for (const i of integrations ?? []) {
        if (!isUserVisibleProvider(i))
            continue;
        const channels = Array.isArray(i.settings?.sync?.channels)
            ? i.settings.sync.channels
            : [];
        for (const ch of channels) {
            if (typeof ch?.type !== 'string' || ch.type === 'warmup')
                continue;
            out.push({
                integration: i,
                channel: ch,
                rowKey: `${i.id}::${ch.outboundProviderId ?? ch.type}`,
            });
        }
    }
    return out;
}
/**
 * Row-level health is derived from the mirror row's status + sync
 * breadcrumbs. Every field read comes from data the client already has
 * locally, so no per-row API call is needed. MUST NOT be reimplemented
 * per consumer — this is the single source of truth so summary counts
 * match the table.
 *
 *   settings.sync.crmSyncError present → 'critical'
 *   status === 'connected'             → 'healthy'
 *   status === 'disconnected'          → 'warning'  (matches "Suspended")
 *   otherwise                          → null       (Unknown)
 */
export function deriveHealthLevel(i) {
    const err = i.settings?.sync?.crmSyncError;
    if (typeof err === 'string' && err.length > 0)
        return 'critical';
    if (i.status === 'connected')
        return 'healthy';
    if (i.status === 'disconnected')
        return 'warning';
    return null;
}
/**
 * Row-status → visible label. Same mapping used across surfaces.
 *   connected → "Active"
 *   disconnected → "Suspended"
 *   anything else → the raw status string or "Unknown"
 */
export function statusLabel(status) {
    if (status === 'connected')
        return 'Active';
    if (status === 'disconnected')
        return 'Suspended';
    return status || 'Unknown';
}
/** Summary counters for the header cards. Reads directly from filtered rows. */
export function computeSummary(integrations) {
    const rows = expandVisibleProviderRows(integrations);
    let active = 0;
    let suspended = 0;
    for (const r of rows) {
        if (r.integration.status === 'connected')
            active += 1;
        else if (r.integration.status === 'disconnected')
            suspended += 1;
    }
    return { total: rows.length, active, suspended };
}
//# sourceMappingURL=index.js.map